import React from 'react';
import { ArrowRight, Play, CheckCircle, Zap, Shield, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Hero: React.FC = () => {
  const navigate = useNavigate();
  const features = [
    { icon: <CheckCircle size={16} />, text: '99.9% 可用性保证' },
    { icon: <CheckCircle size={16} />, text: '秒级弹性扩容' },
    { icon: <CheckCircle size={16} />, text: '24/7 技术支持' },
  ];

  const cards = [
    {
      title: '实时性能监控',
      icon: <BarChart3 className="w-6 h-6" />,
      stats: {
        number: '99.9%',
        label: '系统可用性'
      },
      isWide: true
    },
    {
      title: '安全保障',
      icon: <Shield className="w-6 h-6" />,
      stats: {
        number: '256bit',
        label: '数据加密'
      }
    },
    {
      title: '计算加速',
      icon: <Zap className="w-6 h-6" />,
      stats: {
        number: '10x',
        label: '性能提升'
      }
    }
  ];

  return (
    <section className="relative min-h-screen flex items-center bg-hero-gradient text-white overflow-hidden pt-16 lg:pt-20 xl:pt-24 2xl:pt-28 portrait:pt-12">
      {/* Background Grid Animation */}
      <div className="absolute inset-0 opacity-10 portrait:opacity-5">
        <div 
          className="w-full h-full bg-grid-pattern animate-move-grid"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      <div className="container-responsive relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 2xl:gap-20 portrait:gap-6 items-center min-h-[calc(100vh-5rem)] py-8 lg:py-12 xl:py-16 2xl:py-20 portrait:py-6">
          {/* Left Content */}
          <div className="space-y-6 lg:space-y-8 xl:space-y-10 2xl:space-y-12 portrait:space-y-4">
            <div className="space-y-4 lg:space-y-6 xl:space-y-8 portrait:space-y-3">
              <h1 className="text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl 3xl:text-7xl portrait:text-2xl font-bold leading-tight">
                全栈
                <span className="block bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                  Cloud-HPC
                </span>
                数字化研发平台
              </h1>
              
              <p className="text-base lg:text-lg xl:text-xl 2xl:text-2xl portrait:text-sm text-white/90 leading-relaxed max-w-2xl">
                让高性能计算更简单、更高效。提供从底层硬件到上层应用的完整解决方案，
                助力科研与工业创新。
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row lg:flex-row gap-3 lg:gap-4 xl:gap-6 portrait:gap-2">
              <button
                className="group flex items-center justify-center gap-2 lg:gap-3 xl:gap-4 portrait:gap-2 bg-white text-gray-900 px-6 lg:px-8 xl:px-10 2xl:px-12 portrait:px-4 py-3 lg:py-4 xl:py-5 2xl:py-6 portrait:py-2 rounded-lg lg:rounded-xl xl:rounded-2xl portrait:rounded-lg font-semibold text-sm lg:text-lg xl:text-xl 2xl:text-2xl portrait:text-sm transition-all duration-300 hover:shadow-2xl hover:scale-105"
                onClick={() => navigate('/console')}
              >
                <span>立即开始</span>
                <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5 xl:w-6 xl:h-6 portrait:w-3 portrait:h-3 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
              
              <button
                className="group flex items-center justify-center gap-2 lg:gap-3 xl:gap-4 portrait:gap-2 border-2 border-white/30 text-white hover:bg-white/10 px-6 lg:px-8 xl:px-10 2xl:px-12 portrait:px-4 py-3 lg:py-4 xl:py-5 2xl:py-6 portrait:py-2 rounded-lg lg:rounded-xl xl:rounded-2xl portrait:rounded-lg font-semibold text-sm lg:text-lg xl:text-xl 2xl:text-2xl portrait:text-sm transition-all duration-300"
                onClick={() => navigate('/demo')}
              >
                <Play className="w-4 h-4 lg:w-5 lg:h-5 xl:w-6 xl:h-6 portrait:w-3 portrait:h-3 group-hover:scale-110 transition-transform duration-300" />
                <span>观看演示</span>
              </button>
            </div>

            {/* Features */}
            <div className="flex flex-wrap gap-4 lg:gap-6 xl:gap-8 portrait:gap-3">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2 lg:gap-3 portrait:gap-1 text-white/90">
                  <span className="text-green-400">{React.cloneElement(feature.icon as React.ReactElement, { 
                    size: window.matchMedia('(orientation: portrait)').matches ? 12 : 16 
                  })}</span>
                  <span className="text-xs lg:text-sm xl:text-base portrait:text-xs font-medium">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Visual */}
          <div className="relative">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 xl:gap-8 2xl:gap-10 portrait:gap-3">
              {cards.map((card, index) => (
                <div
                  key={index}
                  className={`glass-card p-4 lg:p-6 xl:p-8 portrait:p-3 transition-all duration-500 hover:scale-105 animate-float ${
                    card.isWide ? 'lg:col-span-2' : ''
                  }`}
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  {/* Card Header */}
                  <div className="flex items-center gap-3 lg:gap-4 xl:gap-6 portrait:gap-2 mb-3 lg:mb-4 xl:mb-6 portrait:mb-2">
                    <div className="p-2 lg:p-3 xl:p-4 2xl:p-5 portrait:p-1.5 bg-white/20 rounded-lg xl:rounded-xl portrait:rounded-md">
                      {React.cloneElement(card.icon as React.ReactElement, { 
                        className: "w-4 h-4 lg:w-5 lg:h-5 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 portrait:w-3 portrait:h-3" 
                      })}
                    </div>
                    <h3 className="text-sm lg:text-base xl:text-lg 2xl:text-xl portrait:text-xs font-semibold">{card.title}</h3>
                  </div>

                  {/* Stats */}
                  <div className="text-center">
                    <div className="text-lg lg:text-xl xl:text-2xl 2xl:text-3xl portrait:text-base font-bold text-white mb-1 lg:mb-2 portrait:mb-1">
                      {card.stats.number}
                    </div>
                    <div className="text-xs lg:text-sm xl:text-base portrait:text-xs text-white/80">
                      {card.stats.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-6 -right-6 lg:-top-8 lg:-right-8 xl:-top-12 xl:-right-12 2xl:-top-16 2xl:-right-16 portrait:-top-4 portrait:-right-4 w-16 h-16 lg:w-20 lg:h-20 xl:w-32 xl:h-32 2xl:w-40 2xl:h-40 portrait:w-12 portrait:h-12 bg-gradient-to-br from-yellow-400/20 to-orange-500/20 rounded-full blur-xl animate-pulse" />
            <div className="absolute -bottom-6 -left-6 lg:-bottom-8 lg:-left-8 xl:-bottom-12 xl:-left-12 2xl:-bottom-16 2xl:-left-16 portrait:-bottom-4 portrait:-left-4 w-12 h-12 lg:w-16 lg:h-16 xl:w-24 xl:h-24 2xl:w-32 2xl:h-32 portrait:w-10 portrait:h-10 bg-gradient-to-br from-blue-400/20 to-purple-500/20 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }} />
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-6 lg:bottom-8 xl:bottom-12 2xl:bottom-16 portrait:bottom-4 left-1/2 transform -translate-x-1/2">
        <div className="w-5 h-8 lg:w-6 lg:h-10 xl:w-8 xl:h-12 2xl:w-10 2xl:h-14 portrait:w-4 portrait:h-6 border-2 border-white/30 rounded-full flex justify-center">
          <div className="w-1 h-2 lg:w-1.5 lg:h-3 xl:w-2 xl:h-4 2xl:w-2.5 2xl:h-5 portrait:w-0.5 portrait:h-1.5 bg-white/60 rounded-full mt-1.5 lg:mt-2 xl:mt-3 2xl:mt-4 portrait:mt-1 animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default Hero; 